 
import {
  AHSeparator,
  AHTeamIconCircleRelay,
  AHText,
  Box,
  ListItem,
  useSpaceSize,
  useTheme,
} from "arkh";
import React, { useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { FlatList } from "react-native";
import { OrgSearchInput } from "../OrgSearchInput";
import { distinct, notEmpty } from "@/src/utils";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { ReceiverStoresSelector_receiverStores$key } from "./__generated__/ReceiverStoresSelector_receiverStores.graphql";
import { ReceiverStoresSelectorListItem_receiverStore$key } from "./__generated__/ReceiverStoresSelectorListItem_receiverStore.graphql";
import { OrgStoreWorkspaceBreadcrumbs } from "../OrgStores/OrgStoreWorkspaceBreadcrumbs";
import { ReceiverStoresSelectorTeamsIndicator_teams$key } from "./__generated__/ReceiverStoresSelectorTeamsIndicator_teams.graphql";
import { ReceiverStoresSelectorMain_receiverStore$key } from "./__generated__/ReceiverStoresSelectorMain_receiverStore.graphql";
import { listGutterStyle } from "../guterStyle";
import { FormattedMessage } from "react-intl";

export function ReceiverStoresSelector(props: {
  stores: ReceiverStoresSelector_receiverStores$key;
  selectedStoreIds: string[];
  disabled?: boolean;
  onStoreSetSelect: (storeIds: string[]) => void;
  isTopicPrivate: boolean;
}) {
  const stores = useFragment(
    graphql`
      fragment ReceiverStoresSelector_receiverStores on ReceiverStore
      @relay(plural: true) {
        store {
          id
          name
          workspaceBreadcrumbs(includeExternalId: true)
        }
        ...ReceiverStoresSelectorListItem_receiverStore
      }
    `,
    props.stores
  );

  const { onStoreSetSelect } = props;

  const selectedStoreIds = props.selectedStoreIds;

  const onToggleStore = (storeId: string) => {
    onStoreSetSelect(
      selectedStoreIds.includes(storeId)
        ? selectedStoreIds.filter((id) => id !== storeId)
        : [...selectedStoreIds, storeId]
    );
  };
  const [search, setSearch] = useState("");
  const [order] = useState<ReceiverStoresSelectorder>({
    column: "name",
    order: "ASC",
  });

  const filteredStores = useMemo(() => {
    return search.length > 0
      ? stores.filter(({ store }) => {
          const bc = store.workspaceBreadcrumbs.join(" ").toLowerCase();
          return (
            store.name.toLowerCase().includes(search.toLowerCase()) ||
            bc.includes(search.toLowerCase())
          );
        })
      : stores;
  }, [search, stores]);

  const mappedStores = filteredStores.map(
    (store) => [store, selectedStoreIds.includes(store.store.id)] as const
  );

  const sortedStores = mappedStores.sort((a, b) => {
    if (order.column === "name") {
      return (
        a[0].store.name.localeCompare(b[0].store.name) *
        (order.order === "ASC" ? -1 : 1)
      );
    }

    if (order.column === "selected") {
      return (
        (a[1] === b[1]
          ? a[0].store.name.localeCompare(b[0].store.name)
          : a[1]
            ? -1
            : 1) * (order.order === "ASC" ? -1 : 1)
      );
    }

    return 0;
  });

  const selected = useMemo(() => {
    if (selectedStoreIds.length === stores.length) {
      return true;
    }
    if (selectedStoreIds.length === 0) {
      return false;
    }
    return "indeterminate";
  }, [selectedStoreIds, stores.length]);

  const selectedIntent = useMemo(() => {
    return filteredStores.every((f) => selectedStoreIds.includes(f.store.id));
  }, [filteredStores, selectedStoreIds]);

  const theme = useTheme();
  const paddingRight = useSpaceSize("medium");
  const intl_tokens = useIntlTokens();
  return (
    <>
      <Box color="surface-primary">
        <OrgSearchInput
          value={search}
          onChangeText={setSearch}
          mode="integrated"
        />
        <AHSeparator />

        <ListItem
          onPress={() => {
            if (selectedIntent === false) {
              onStoreSetSelect(
                distinct([
                  ...selectedStoreIds,
                  ...filteredStores.map((f) => f.store.id),
                ])
              );
            } else {
              onStoreSetSelect(
                selectedStoreIds.filter(
                  (id) => !filteredStores.map((f) => f.store.id).includes(id)
                )
              );
            }
          }}
          left={<ListItem.MultiSelectIndicator selected={selected} />}
        >
          <ListItem.TitleSecondary>
            {intl_tokens.Units}{" "}
            <AHText type="detail">
              ( {selectedStoreIds.length} / {stores.length} )
            </AHText>
          </ListItem.TitleSecondary>
        </ListItem>
        <AHSeparator />
      </Box>
      <FlatList
        data={sortedStores}
        style={[
          {
            backgroundColor: theme["surface-primary"],
          },
          listGutterStyle,
        ]}
        contentContainerStyle={{
          paddingRight: paddingRight - 8,
          paddingBottom: paddingRight,
        }}
        keyboardShouldPersistTaps="handled"
        renderItem={({ item: [store, selected], index }) => {
          return (
            <StoreReceiverListItem
              disabled={props.disabled}
              onPress={onToggleStore}
              store={store}
              isLast={index === stores.length - 1}
              selected={selected}
              isPrivate={props.isTopicPrivate}
            />
          );
        }}
      />
    </>
  );
}

type StoreReceiverSelectSort = "name" | "selected";

type ReceiverStoresSelectorder = {
  column: StoreReceiverSelectSort;
  order: "ASC" | "DESC";
};

export function StoreReceiverListItem(props: {
  store: ReceiverStoresSelectorListItem_receiverStore$key;
  selected: boolean;
  isLast: boolean;
  onPress: (storeId: string) => void;
  disabled?: boolean;
  isPrivate: boolean;
}) {
  const data = useFragment(
    graphql`
      fragment ReceiverStoresSelectorListItem_receiverStore on ReceiverStore {
        store {
          ...ReceiverStoresSelectorMain_receiverStore
        }
        routedTeams {
          ...ReceiverStoresSelectorTeamsIndicator_teams
        }
      }
    `,
    props.store
  );

  const routedTeams = (data.routedTeams ?? []).filter(notEmpty);
  return (
    <ReceiverStoreMain
      {...props}
      store={data.store}
      right={
        props.selected ? (
          routedTeams.length === 0 ? (
            props.isPrivate ? (
              <ReceiverStoreNoTeamsIndicator />
            ) : (
              <ReceiverStoreAllTeamsIndicator />
            )
          ) : (
            <ReceiverStoreTeamsIndicator teams={routedTeams} />
          )
        ) : null
      }
    />
  );
}

export function ReceiverStoreAllTeamsIndicator() {
  const intl_token = useIntlTokens();
  return <AHText type="detail">{intl_token.AllTeams}</AHText>;
}

export function ReceiverStoreNoTeamsIndicator() {
  return (
    <AHText type="detail" color="negative">
      <FormattedMessage
        defaultMessage="No routed teams"
        description="ReceiverStoreNoTeamsIndicator"
      />
    </AHText>
  );
}

export function ReceiverStoreMain(props: {
  store: ReceiverStoresSelectorMain_receiverStore$key;
  selected: boolean;
  isLast: boolean;
  onPress: (storeId: string) => void;
  disabled?: boolean;
  right?: React.ReactNode;
}) {
  const store = useFragment(
    graphql`
      fragment ReceiverStoresSelectorMain_receiverStore on Store {
        id
        name
        ...OrgStoreWorkspaceBreadcrumbs_store
      }
    `,
    props.store
  );

  return (
    <ListItem
      isLast={props.isLast}
      onPress={() => {
        props.onPress(store.id);
      }}
      disabled={props.disabled}
      left={
        <ListItem.MultiSelectIndicator
          disabled={props.disabled}
          selected={props.selected}
        />
      }
      right={props.right}
    >
      <Box
        style={{
          flex: 1,
          flexDirection: "row",

          alignItems: "center",
        }}
      >
        <Box spaceRight="medium">
          <OrgStoreWorkspaceBreadcrumbs store={store} />
          <AHText type="subtitle" numberOfLines={1}>
            {store.name}
          </AHText>
        </Box>
      </Box>
    </ListItem>
  );
}

export function ReceiverStoreTeamsIndicator(props: {
  teams: ReceiverStoresSelectorTeamsIndicator_teams$key;
}) {
  const teams = useFragment(
    graphql`
      fragment ReceiverStoresSelectorTeamsIndicator_teams on Team
      @relay(plural: true) {
        id
        name
        ...AHTeamIconCircle
      }
    `,
    props.teams
  );
  return (
    <Box style={{ flexDirection: "row", alignItems: "center" }}>
      {teams?.filter(notEmpty).map((t) => (
        <Box
          key={t.id}
          spaceRight="small"
          style={{ flexDirection: "row", alignItems: "center" }}
        >
          <AHTeamIconCircleRelay team={t} size="small" />
          <AHText spaceLeft="xsmall" type="detail">
            {t.name}
          </AHText>
        </Box>
      ))}
    </Box>
  );
}
